import type { TypedPropertyFilterQuery } from '@risksmart-app/components/src/table/tableUtils';
import { isEqual, uniqWith } from 'lodash';
import { useCallback, useMemo } from 'react';
import { merge } from 'ts-deepmerge';

import { emptyFilterQuery } from '@/utils/table/types';

import { Gigawidget } from '../gigawidget';
import type {
  Category,
  CategoryType,
  DataSourceItem,
  DateFilterOptions,
  GigawidgetCommonProps,
  WidgetDataSource,
} from '../gigawidget/types';
import { default as useOwnershipClickthroughEnabled } from '../my-items/hooks/useGetClickthroughEnabled';
import { useGetMyItemsFilteringTokens } from '../my-items/hooks/useGetMyItemsFilteringTokens';
import type { DashboardFilter } from '../useDashboardStore';
import { useDashboardFilters } from '../useDashboardStore';
import { RelationalClickthroughBanner } from './RelationalClickthroughBanner';
import { useAutoDismissBanner } from './useAutoDismissBanner';
import type { GigawidgetSettings } from './util';
import {
  convertToTokenGroups,
  dashboardFilterToQuery,
  dateFormats,
  getCategoryGetter,
  wrapOwnershipTokens,
} from './util';

type Props<TDataSource extends WidgetDataSource> = {
  dataSource: TDataSource;
  settings: GigawidgetSettings;
};

export const UniversalChart = <TDataSource extends WidgetDataSource>({
  dataSource,
  settings,
}: Props<TDataSource>) => {
  const filters = useDashboardFilters();
  const { getMyItemsFilteringTokens } = useGetMyItemsFilteringTokens();
  const ownershipClickthroughEnabled = useOwnershipClickthroughEnabled();
  const datePrecision = settings.precision ?? 'month';

  // Relational data source widgets cannot click through to a single register, so
  // their click-through is disabled. The disabled state is communicated by an
  // in-widget banner (RSP-5383, rendered below), replacing the RSP-5304 info
  // popover. The relational message must NOT flow to the leaf widgets' own
  // popover (AC13), so it is suppressed here; the per-widget
  // `noClickthroughMessageContent` override (used by My Items) still flows for
  // non-relational widgets.
  const isRelationalDataSource = dataSource.group === 'relationship';

  // Owns the in-widget banner's visibility + auto-dismiss timer. The banner is
  // triggered at the click-through seam (the relational `onClickUrl` below), not
  // by intercepting DOM events — so it fires only on a genuine click-through
  // attempt and never on table chrome (sort / resize / settings).
  const { visible: bannerVisible, trigger: triggerBanner } =
    useAutoDismissBanner();

  // For relational widgets, a navigable click resolves to no URL — surface the
  // banner explaining why and return '' so the leaf widget's `if (url)
  // navigate(url)` no-ops. Tables pass no `onClickUrl`, so they never reach here.
  const showRelationalDisabledBanner = useCallback((): string => {
    triggerBanner();

    return '';
  }, [triggerBanner]);

  // Memoised at the call site rather than inside `getCategoryGetter`, which
  // stays a plain function: for a `custom:` id it constructs a new object with
  // a new `categoryGetter` arrow every call, and that identity is a dependency
  // of the `useAggregateCategories` and `useDataSeries` memos (ENG-560).
  const categoryConfig = useMemo(
    () =>
      getCategoryGetter(settings.categoryGetter, dataSource.categoryGetters),
    [settings.categoryGetter, dataSource.categoryGetters]
  );

  const subCategoryConfig = useMemo(
    () =>
      getCategoryGetter(settings.subCategoryGetter, dataSource.categoryGetters),
    [settings.subCategoryGetter, dataSource.categoryGetters]
  );

  const dateFilterOptions: DateFilterOptions<TDataSource> = {
    dateFilter: categoryConfig?.date
      ? categoryConfig.dashboardDateFilterOverride
      : dataSource.dashboardFilterConfig.dateFilter,
    precision: categoryConfig?.date ? datePrecision : 'day',
    dateFormat: dateFormats[datePrecision],
  };

  const clickThroughUrl =
    settings.allowOwnershipFiltering && !ownershipClickthroughEnabled
      ? undefined
      : (
          item: Category<DataSourceItem<TDataSource>, CategoryType>,
          _filters?: DashboardFilter,
          subCategory?: Category<DataSourceItem<TDataSource>, CategoryType>
        ) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const propertyFilter: TypedPropertyFilterQuery<any> = merge(
            settings.filtering
              ? convertToTokenGroups(settings.filtering)
              : emptyFilterQuery,

            settings.allowOwnershipFiltering
              ? wrapOwnershipTokens(getMyItemsFilteringTokens(item.data))
              : emptyFilterQuery,

            !settings.allowOwnershipFiltering
              ? dashboardFilterToQuery(
                  filters,
                  datePrecision,
                  !categoryConfig?.date
                    ? dataSource.dashboardFilterConfig.dateClickthroughFilter
                    : undefined,
                  {
                    departments:
                      !!dataSource.dashboardFilterConfig.departmentsFilter,
                    tags: !!dataSource.dashboardFilterConfig.tagsFilter,
                  }
                )
              : emptyFilterQuery,
            {
              tokenGroups:
                categoryConfig?.clickthroughFilter?.(item, datePrecision) ?? [],
            },
            // When a stacked segment is clicked, also filter by the clicked
            // subcategory using its own clickthroughFilter (RSP-6045).
            subCategory
              ? {
                  tokenGroups:
                    subCategoryConfig?.clickthroughFilter?.(
                      subCategory,
                      datePrecision
                    ) ?? [],
                }
              : emptyFilterQuery
          );

          return (
            dataSource.clickThroughUrl?.({
              ...propertyFilter,
              // Remove empty token groups
              tokenGroups: uniqWith(
                propertyFilter.tokenGroups ?? [],
                isEqual
              ).filter((tg) => !('tokens' in tg) || tg.tokens.length > 0),
            }) ?? ''
          );
        };

  const onClickNumberHandler =
    settings.allowOwnershipFiltering && !ownershipClickthroughEnabled
      ? undefined
      : (data: readonly DataSourceItem<TDataSource>[] | undefined) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const propertyFilter: TypedPropertyFilterQuery<any> = merge(
            settings.filtering
              ? convertToTokenGroups(settings.filtering)
              : emptyFilterQuery,
            settings.allowOwnershipFiltering
              ? wrapOwnershipTokens(getMyItemsFilteringTokens(data))
              : emptyFilterQuery,
            !settings.allowOwnershipFiltering
              ? dashboardFilterToQuery(
                  filters,
                  'day',
                  settings.ignoreDashboardDateFilter
                    ? undefined
                    : dataSource.dashboardFilterConfig.dateClickthroughFilter,
                  {
                    departments:
                      !!dataSource.dashboardFilterConfig.departmentsFilter,
                    tags: !!dataSource.dashboardFilterConfig.tagsFilter,
                  }
                )
              : emptyFilterQuery
          );

          return (
            dataSource.clickThroughUrl?.(
              {
                ...propertyFilter,
                // Remove empty token groups
                tokenGroups: uniqWith(
                  propertyFilter.tokenGroups ?? [],
                  isEqual
                ).filter((tg) => !('tokens' in tg) || tg.tokens.length > 0),
              },
              settings.sorting ?? undefined
            ) ?? ''
          );
        };

  // Relational widgets route a navigable click to the banner instead of a URL;
  // single-source widgets keep their real click-through resolver.
  const chartOnClickUrl = isRelationalDataSource
    ? showRelationalDisabledBanner
    : clickThroughUrl;
  const numberOnClickUrl = isRelationalDataSource
    ? showRelationalDisabledBanner
    : onClickNumberHandler;

  const commonProps: GigawidgetCommonProps<TDataSource> = {
    dataSource,
    dateFilterOptions,
    propertyFilterQuery: settings.filtering ?? emptyFilterQuery,
    allowOwnershipFiltering: settings.allowOwnershipFiltering,
    // Relational widgets show the banner instead of the leaf popover (AC13);
    // non-relational widgets keep their per-widget popover message (e.g. the My
    // Items inherited-filters notice).
    noClickthroughMessageContent: isRelationalDataSource
      ? undefined
      : settings.noClickthroughMessageContent,
  };

  const renderChart = () => {
    //categorical charts
    if (categoryConfig) {
      switch (settings.chartType) {
        case 'pie':
        case 'donut':
          return (
            <Gigawidget
              {...commonProps}
              type={settings.chartType}
              aggregationType={settings.aggregationType}
              aggregationField={settings.aggregationField}
              categoryGetter={categoryConfig.categoryGetter}
              categoryRatingTranslationKey={categoryConfig.ratingColourKey}
              categoryOverrideFunction={categoryConfig.categoryOverrideFunction}
              onClickUrl={chartOnClickUrl}
              labelDisplay={settings.labelDisplay}
            />
          );
        case 'bar':
        case 'stacked-bar':
          return (
            <Gigawidget
              {...commonProps}
              aggregationType={settings.aggregationType}
              aggregationField={settings.aggregationField}
              type={settings.chartType}
              categoryGetter={categoryConfig.categoryGetter}
              categoryRatingTranslationKey={categoryConfig.ratingColourKey}
              categoryOverrideFunction={categoryConfig.categoryOverrideFunction}
              subCategoryGetter={subCategoryConfig?.categoryGetter}
              subCategoryRatingTranslationKey={
                subCategoryConfig?.ratingColourKey
              }
              subCategoryOverrideFunction={
                subCategoryConfig?.categoryOverrideFunction
              }
              orientation={
                settings.invertBarChartAxis ? 'horizontal' : 'vertical'
              }
              onClickUrl={chartOnClickUrl}
            />
          );
        case 'radar':
          return (
            <Gigawidget
              {...commonProps}
              aggregationType={settings.aggregationType}
              aggregationField={settings.aggregationField}
              type={settings.chartType}
              categoryGetter={categoryConfig.categoryGetter}
              categoryRatingTranslationKey={categoryConfig.ratingColourKey}
              categoryOverrideFunction={categoryConfig.categoryOverrideFunction}
              subCategoryGetter={subCategoryConfig?.categoryGetter}
              subCategoryRatingTranslationKey={
                subCategoryConfig?.ratingColourKey
              }
              subCategoryOverrideFunction={
                subCategoryConfig?.categoryOverrideFunction
              }
              onClickUrl={chartOnClickUrl}
            />
          );
        case 'line':
          return (
            <Gigawidget
              {...commonProps}
              aggregationType={settings.aggregationType}
              aggregationField={settings.aggregationField}
              type={settings.chartType}
              categoryGetter={categoryConfig.categoryGetter}
              categoryRatingTranslationKey={categoryConfig.ratingColourKey}
              categoryOverrideFunction={categoryConfig.categoryOverrideFunction}
              subCategoryGetter={subCategoryConfig?.categoryGetter}
              subCategoryRatingTranslationKey={
                subCategoryConfig?.ratingColourKey
              }
              subCategoryOverrideFunction={
                subCategoryConfig?.categoryOverrideFunction
              }
              onClickUrl={chartOnClickUrl}
            />
          );
      }
    }

    // non-categorical charts
    switch (settings.chartType) {
      case 'table':
        return (
          <Gigawidget
            {...commonProps}
            type={'table'}
            aggregationField={settings.aggregationField}
            aggregationType={settings.aggregationType}
          />
        );
      case 'kpi':
        if (!settings.aggregationType) {
          return null;
        }

        return (
          <Gigawidget
            {...commonProps}
            type={'kpi'}
            onClickUrl={numberOnClickUrl}
            aggregationField={settings.aggregationField}
            aggregationType={settings.aggregationType}
            unit={settings.customUnit ? settings.unit : undefined}
          />
        );
      case 'placemat':
        return <Gigawidget {...commonProps} type={'placemat'} />;
    }

    return null;
  };

  const chart = renderChart();

  // `[&>span]:!contents [&>span>span]:!contents` neutralises the inline-block
  // `<span>` trigger wrappers Cloudscape's `Popover` injects: non-relational
  // popover-backed widgets (e.g. KPI / ValueWidget with
  // `noClickthroughMessageContent`) render that leaf popover, and without this
  // guard the chart would be width-capped at the spans' intrinsic content width.
  const layoutGuard = '[&>span]:!contents [&>span>span]:!contents';

  // Relational widgets render the in-widget banner (RSP-5383) on a click-through
  // attempt — triggered by `showRelationalDisabledBanner` above, anchored to the
  // top of the widget. `relative` is only needed to anchor that absolute banner,
  // so non-relational widgets keep the exact static wrapper they had before.
  return (
    <div
      className={`size-full ${layoutGuard}${
        isRelationalDataSource ? ' relative' : ''
      }`}
    >
      {chart}
      {isRelationalDataSource && (
        <RelationalClickthroughBanner visible={bannerVisible} />
      )}
    </div>
  );
};
